import React from 'react';

import styled from '@emotion/styled';
import { Navigate } from 'react-router-dom';
import { AwardingBodyIdentifiers, ROUTES } from 'core';
import { selectPreparationCentre, selectSelectedCountryId } from 'store/country/countrySlice';
import { useAppSelector } from 'store';
import {
  selectSelectedAwardingBody,
  selectSelectedQualificationLevel,
  selectSelectedSession,
} from 'store/exams-search/examsSearchSlice';
import DOMPurify from 'dompurify';

import { MainContainer, PageContainer, Quote } from '@britishcouncil/react-solas-ors3';

import {
  AwardingBodiesSelect,
  ExamsList,
  LocationSelect,
  QualificationLevelSelect,
  SessionSelect,
  SubjectSelect,
  Timeline,
} from './components';

export const ExamsSearch = () => {
  const country = useAppSelector(selectSelectedCountryId);
  const pcInfo = useAppSelector(selectPreparationCentre);
  const selectedSession = useAppSelector(selectSelectedSession);
  const selectedAwardingBodyId = useAppSelector(selectSelectedAwardingBody);
  const selectedQualificationLevelId = useAppSelector(selectSelectedQualificationLevel);

  if (!country) {
    return <Navigate to={ROUTES.COUNTRY_SELECTOR} />;
  }

  return (
    <>
      <MainContainer>
        <h1>Search for exams</h1>
      </MainContainer>
      {selectedSession &&
        (+selectedQualificationLevelId > 0 || +selectedAwardingBodyId !== AwardingBodyIdentifiers.Edexcel) && (
          <TimelineWrapper>
            <PageContainer>
              <Timeline
                sessionId={+selectedSession.sessionId}
                awardingBodyId={+selectedAwardingBodyId}
                qualificationLevelId={+selectedQualificationLevelId}
              />
            </PageContainer>
          </TimelineWrapper>
        )}
      <StyledContainer>
        <PageContainer>
          {pcInfo && pcInfo.disclaimerEnabled && (
            <Quote intent="warning" title={`${pcInfo.preparationCentreName} disclaimer`}>
              <p>
                The British Council takes no responsibility for all exam entries. Third parties are held responsible for
                all their candidate entries. Please check with your school for entry issues and/or mishaps before
                confirming registration.
              </p>
              <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(pcInfo.disclaimer) }} />
            </Quote>
          )}
          <AwardingBodiesSelect />
          <SessionSelect />
          <QualificationLevelSelect />
          <LocationSelect />
          <SubjectSelect />
          <ExamsList />
        </PageContainer>
      </StyledContainer>
    </>
  );
};

const StyledContainer = styled.div`
  label + div {
    margin-bottom: 32px;
  }

  & .loader-container {
    margin-top: 37px;
  }

  /** Nasty hack for missing className setter in PageContainer and being unable to override using styled */
  & > div {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-height: 930px;

    & > div {
      display: flex;
      flex-direction: column;
      flex-grow: 1;

      & > [data-testid='page-container'] {
        flex-grow: 1;
        width: 100%;
      }
    }
  }
`;

const TimelineWrapper = styled.div`
  & > div {
    padding-bottom: 0;
  }

  .timeline {
    @media (min-width: 850px) {
      margin: 0 -150px;
    }
  }
`;
